import { useId } from "react";
import {
  createIsometric,
  type Point,
  points,
  roundedFootprint,
  roundedPath,
} from "src/lib/Isometric";
import styles from "./StatusIllustration.module.scss";

export type StatusIllustrationScene =
  | "not-found"
  | "unauthorized"
  | "unknown"
  | "network-error"
  | "maintenance";

export interface StatusIllustrationProps {
  scene: StatusIllustrationScene;
  /** Status code printed on the scene's slab, such as "404". */
  code?: string;
  className?: string;
}

// Drawn in the landing hero's isometric style: white rounded slabs lit from the top left, soft
// contact shadows, ground tracks and dotted landing pads, with brand colour used sparingly.
const { iso, topFace, roundedBox } = createIsometric(200, 128);

const SLAB_RADIUS = 10;
// The code slab sits back left; each scene's subject sits front right.
const CODE_AT: Point = [-72, 30];
const SUBJECT_AT: Point = [72, -30];
const LAYER_HEIGHT = 8;
const LAYER_GAP = 4;

type Ids = { side: string; accentSide: string; shadow: string };

const toScreen = (footprint: Point[], z = 0) => points(...footprint.map(([x, y]) => iso(x, y, z)));

const Shadow = (props: { at: Point; half: number; ids: Ids; className?: string }) => {
  const { at, half, ids, className } = props;
  // Offset away from the top-left light, like the hero's cast shadows.
  const footprint = roundedFootprint(at[0] + 5, at[1] + 2, half, half, SLAB_RADIUS);
  return (
    <polygon
      points={toScreen(footprint)}
      className={className ?? styles.shadow}
      filter={`url(#${ids.shadow})`}
    />
  );
};

const Box = (props: {
  at: Point;
  half: number;
  z0?: number;
  z1: number;
  radius?: number;
  ids: Ids;
  topClassName?: string;
}) => {
  const { at, half, z0 = 0, z1, radius = SLAB_RADIUS, ids, topClassName } = props;
  const { top, side } = roundedBox(at[0], at[1], half, half, z0, z1, radius);
  return (
    <>
      <polygon points={side} fill={`url(#${ids.side})`} />
      <polygon points={top} className={topClassName ?? styles.top} />
    </>
  );
};

const CodeSlab = (props: { code: string; ids: Ids; at?: Point; half?: number }) => {
  const { code, ids, at = CODE_AT, half = 52 } = props;
  const height = 16;
  return (
    <g>
      <Shadow at={at} half={half} ids={ids} />
      <Box at={at} half={half} z1={height} ids={ids} />
      <g transform={topFace(at[0], at[1], height)}>
        <text className={styles.code} textAnchor="middle" dominantBaseline="central">
          {code}
        </text>
      </g>
    </g>
  );
};

/** A ground-level landing zone with a dotted outline, like the hero's upload pad. */
const LandingPad = (props: { at: Point; half: number }) => {
  const footprint = roundedFootprint(props.at[0], props.at[1], props.half, props.half, SLAB_RADIUS);
  return (
    <g>
      <polygon points={toScreen(footprint)} className={styles.padFill} />
      <polygon points={toScreen(footprint)} className={styles.padOutline} />
    </g>
  );
};

/** A small asset tile with a glyph on its top face. */
const Tile = (props: {
  at: Point;
  z: number;
  ids: Ids;
  glyph: "document" | "lock";
  accent?: boolean;
}) => {
  const { at, z, ids, glyph, accent } = props;
  const height = 7;
  return (
    <g>
      <Box
        at={at}
        half={20}
        z0={z}
        z1={z + height}
        radius={6}
        ids={accent ? { ...ids, side: ids.accentSide } : ids}
        topClassName={accent ? styles.accentTop : styles.top}
      />
      <g
        transform={topFace(at[0], at[1], z + height)}
        className={accent ? styles.glyphOnAccent : styles.glyph}
      >
        {glyph === "document" ? (
          <>
            <path d="M-9 -12 H4 L10 -6 V12 H-9 Z M4 -12 V-6 H10" />
            <path d="M-5 0 H6 M-5 6 H3" />
          </>
        ) : (
          <>
            <rect x={-8} y={-2} width={16} height={12} rx={2.5} />
            <path d="M-5 -2 V-6 A5 5 0 0 1 5 -6 V-2" />
          </>
        )}
      </g>
    </g>
  );
};

/** A server stack: thin rounded layers with a gap between each. */
const stackLayer = (at: Point, half: number, layer: number) => {
  const z0 = layer * (LAYER_HEIGHT + LAYER_GAP);
  return { z0, z1: z0 + LAYER_HEIGHT, at, half };
};

const Stack = (props: { at: Point; half: number; layers: number; ids: Ids }) => {
  const { at, half, layers, ids } = props;
  return (
    <g>
      {Array.from({ length: layers }, (_, layer) => {
        const { z0, z1 } = stackLayer(at, half, layer);
        return <Box key={layer} at={at} half={half} z0={z0} z1={z1} radius={6} ids={ids} />;
      })}
    </g>
  );
};

const Track = (props: { route: Point[]; className?: string }) => (
  <path
    d={roundedPath(
      props.route.map(([x, y]) => iso(x, y)),
      10,
    )}
    className={props.className ?? styles.track}
  />
);

const SceneNotFound = (props: { ids: Ids; code: string }) => {
  const { ids, code } = props;
  return (
    <>
      <Track
        route={[
          [-20, 30],
          [72, 30],
          [72, 4],
        ]}
      />
      <CodeSlab code={code} ids={ids} />
      <LandingPad at={SUBJECT_AT} half={34} />
      {/* The page's tile hovers over its pad, never quite landing. */}
      <g className={styles.hoverShadow}>
        <Shadow at={SUBJECT_AT} half={20} ids={ids} className={styles.softShadow} />
      </g>
      <g className={styles.hover}>
        <Tile at={SUBJECT_AT} z={30} ids={ids} glyph="document" />
      </g>
    </>
  );
};

const SceneUnauthorized = (props: { ids: Ids; code: string }) => {
  const { ids, code } = props;
  const codeAt: Point = [-80, 30];
  const gateAt: Point = [15, 30];
  const assetAt: Point = [90, -30];
  return (
    <>
      <Track route={[[-44, 30], gateAt]} />
      <Track route={[gateAt, [90, 30], [90, 0]]} className={styles.trackFaint} />
      <CodeSlab code={code} ids={ids} at={codeAt} half={36} />
      <Shadow at={gateAt} half={20} ids={ids} />
      <g className={styles.pulse}>
        <ellipse {...ringAt(gateAt, 26, 0)} className={styles.ring} />
      </g>
      <Tile at={gateAt} z={0} ids={ids} glyph="lock" accent={true} />
      <Shadow at={assetAt} half={30} ids={ids} />
      <Box at={assetAt} half={30} z1={8} ids={ids} />
      <Tile at={assetAt} z={8} ids={ids} glyph="document" />
    </>
  );
};

const ringAt = (at: Point, radius: number, z: number) => {
  const [cx, cy] = iso(at[0], at[1], z);
  return { cx, cy, rx: radius * Math.SQRT2 * 0.866, ry: radius * Math.SQRT1_2 };
};

const SceneUnknown = (props: { ids: Ids; code: string }) => {
  const { ids, code } = props;
  const half = 28;
  const top = stackLayer(SUBJECT_AT, half, 2);
  return (
    <>
      <Track
        route={[
          [-20, 30],
          [72, 30],
          [72, 2],
        ]}
      />
      <CodeSlab code={code} ids={ids} />
      <Shadow at={SUBJECT_AT} half={half} ids={ids} />
      <Stack at={SUBJECT_AT} half={half} layers={2} ids={ids} />
      {/* The top layer has slipped out of place and rocks gently. */}
      <g className={styles.rock}>
        <Box
          at={[SUBJECT_AT[0] + 9, SUBJECT_AT[1] - 3]}
          half={half}
          z0={top.z0}
          z1={top.z1}
          radius={6}
          ids={ids}
        />
      </g>
    </>
  );
};

const SceneNetworkError = (props: { ids: Ids }) => {
  const { ids } = props;
  const from: Point = [-80, 30];
  const to: Point = [80, -30];
  return (
    <>
      {/* The route breaks halfway, so nothing reaches the far stack. */}
      <Track
        route={[
          [-56, 30],
          [-10, 30],
        ]}
      />
      <Track
        route={[
          [18, 30],
          [80, 30],
          [80, -6],
        ]}
        className={styles.trackFaint}
      />
      <g className={styles.packet}>
        <ellipse {...ringAt([-56, 30], 4, 2)} className={styles.packetDot} />
      </g>
      <Shadow at={from} half={24} ids={ids} />
      <Stack at={from} half={24} layers={3} ids={ids} />
      <Shadow at={to} half={24} ids={ids} />
      <g className={styles.dim}>
        <Stack at={to} half={24} layers={3} ids={ids} />
      </g>
    </>
  );
};

const SceneMaintenance = (props: { ids: Ids }) => {
  const { ids } = props;
  const at: Point = [20, -10];
  const half = 34;
  const top = stackLayer(at, half, 2);
  return (
    <>
      <LandingPad at={[-80, 40]} half={26} />
      <Tile at={[-80, 40]} z={0} ids={ids} glyph="document" />
      <Shadow at={at} half={half} ids={ids} />
      <Stack at={at} half={half} layers={2} ids={ids} />
      {/* The top layer is lowered into place, lifted, and lowered again. */}
      <g className={styles.settle}>
        <Box
          at={at}
          half={half}
          z0={top.z0}
          z1={top.z1}
          radius={6}
          ids={ids}
          topClassName={styles.accentTop}
        />
      </g>
    </>
  );
};

/** Decorative isometric scene for full-page status screens. */
const StatusIllustration = (props: StatusIllustrationProps) => {
  const { scene, code = "", className } = props;
  const id = useId().replace(/:/g, "");
  const ids: Ids = {
    side: `${id}-side`,
    accentSide: `${id}-accent-side`,
    shadow: `${id}-shadow`,
  };

  return (
    <svg
      className={[styles.illustration, className].filter(Boolean).join(" ")}
      viewBox="0 0 400 240"
      aria-hidden={true}
      focusable={false}
      data-scene={scene}
    >
      <defs>
        <linearGradient id={ids.side} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0.44" className={styles.sideLeft} />
          <stop offset="0.56" className={styles.sideRight} />
        </linearGradient>
        <linearGradient id={ids.accentSide} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0.44" className={styles.accentSideLeft} />
          <stop offset="0.56" className={styles.accentSideRight} />
        </linearGradient>
        <filter id={ids.shadow} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="4" />
        </filter>
      </defs>
      {scene === "not-found" && <SceneNotFound ids={ids} code={code} />}
      {scene === "unauthorized" && <SceneUnauthorized ids={ids} code={code} />}
      {scene === "unknown" && <SceneUnknown ids={ids} code={code} />}
      {scene === "network-error" && <SceneNetworkError ids={ids} />}
      {scene === "maintenance" && <SceneMaintenance ids={ids} />}
    </svg>
  );
};

export default StatusIllustration;
